import React, { useCallback, useEffect, useRef } from "react";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { REGULATORY_GROUP_KEY } from "@FillInTableContainer/shared/constants/regulatoryKey";
import { ENABLE_GROUP_TABLE_KEY_LIST } from "@FillInTableContainer/shared/constants/tableCreatorGroupTable.constants";
import {
  DEFAULT_FOCUS_STATUS,
  FocusStatus,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import {
  DEFAULT_POSITION_INFO,
  PositionInfo,
  useTableCreatorPositionInfo,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useGetNumberOfGroupShell } from "@FillInTableContainer/shared/hooks/useGetNumberOfGroupShell";
import { isTableCustomTitle } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getTableCustomTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { useHandleApprovalProcessContentsTable } from "./useHandleApprovalProcessContentsTable";
import { useHandleDrugProfileTable } from "./useHandleDrugProfileTable";
import { TableInfoProps } from "../RegulatoryTableCreatorTable";

interface UseHandleTableCreatorTableReturn {
  info: PositionInfo;
  focusStatus: FocusStatus;
  isFocused: boolean;
  isLastTable: boolean;
  isVisibleTableCustomTitle: boolean;
  tableWrapperRef: React.RefObject<HTMLDivElement>;
  tableCustomTitle: string;
  isEnableEdit: boolean;
  isFocusedAddButton: boolean;
  focusEventToChangePositionInfo: (focusEnterType: FocusStatus) => void;
  numberOfGroupShellCount: (groupShellKey: string | null) => number;
  handleClickDeleteButton: () => void;
  changePositionInfo: () => void;
  changePositionInfoToDefault: () => void;
  handleDefaultFocus: () => void;
  changeFocusStatus: (focusStatus: FocusStatus) => void;
  handleFocusGroupShell: (
    groupShellKey: string | null,
    groupShellIndex: number,
  ) => void;
}

export const useHandleRegulatoryTableCreatorTable = (
  tablePropsInfo: TableInfoProps,
): UseHandleTableCreatorTableReturn => {
  const {
    groupTableKey,
    tableKey,
    groupTableIndex,
    tableIndex,
    tableCount,
    groupShellList,
  } = tablePropsInfo;

  const {
    handleClickDrugProfileDeleteButton,
    handleKeyDownToDeleteDrugProfileTable,
  } = useHandleDrugProfileTable(tablePropsInfo);

  const {
    handleClickApprovalProcessContentsDeleteButton,
    handleKeyDownToDeleteApprovalProcessContentsTable,
  } = useHandleApprovalProcessContentsTable(tablePropsInfo);

  const articleSection = "Regulatory";

  const { info, changeInfo } = useTableCreatorPositionInfo();
  const [entryData] = useEntryDataState(getPressIdFromURL());
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { data: articleInfo } = useArticleInfoQuery(
    Number(getPressIdFromURL()),
  );
  const isEnableEdit = articleInfo
    ? ENABLE_GROUP_TABLE_KEY_LIST[articleSection][groupTableKey].includes(
        articleInfo.draftArticleStatus,
      )
    : false;

  const { groupShellCount } = useGetNumberOfGroupShell();
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const isLastTable = tableIndex === tableCount - 1;

  const isFocused =
    info.groupTableIndex === groupTableIndex &&
    info.groupTableKey === groupTableKey &&
    info.tableKey === tableKey &&
    info.tableIndex === tableIndex &&
    info.groupShellKey === null &&
    info.groupShellIndex === null &&
    info.shellKey === null &&
    info.shellIndex === null &&
    info.valueIndex === null &&
    isEnableEdit;

  const isFocusedAddButton =
    focusStatus.container === "noFocus" && isEnableEdit;

  const tableCustomTitle = getTableCustomTitle(
    articleSection,
    entryData,
    groupTableKey,
    tableIndex,
  );

  const isVisibleTableCustomTitle =
    isTableCustomTitle(articleSection, groupTableKey) &&
    groupShellCount(groupShellList[0].groupShellKey, tableIndex) > 1;

  const numberOfGroupShellCount = (groupShellKey: string | null) =>
    groupShellCount(groupShellKey, tableIndex);

  const changePositionInfo = () => {
    changeInfo({
      groupTableKey,
      groupTableIndex,
      tableKey,
      tableIndex,
      groupShellKey: null,
      groupShellIndex: null,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
  };

  const handleDefaultFocus = () => {
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const handleFocusGroupShell = (
    groupShellKey: string | null,
    groupShellIndex: number,
  ) => {
    changeInfo({
      groupTableKey,
      groupTableIndex,
      groupShellKey,
      groupShellIndex,
      tableIndex,
      tableKey,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
    changeFocusStatus({ type: "clear", container: "groupShell" });
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const changePositionInfoToDefault = () => {
    changeInfo(DEFAULT_POSITION_INFO);
  };

  const focusEventToChangePositionInfo = (focusEnterType: FocusStatus) => {
    changeInfo({
      groupTableKey,
      groupTableIndex,
      tableKey,
      tableIndex,
      groupShellKey: null,
      groupShellIndex: null,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
    changeFocusStatus({ type: "clear", container: "table" });

    if (focusEnterType.type === "clear") {
      changeFocusStatus({ type: "clear", container: "table" });
    }
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const handleClickDeleteButton = () => {
    if (info.groupTableKey === REGULATORY_GROUP_KEY.drugProfile)
      handleClickDrugProfileDeleteButton();

    if (info.groupTableKey === REGULATORY_GROUP_KEY.approvalProcessContents)
      handleClickApprovalProcessContentsDeleteButton();
  };

  const handleKeyDownToDeleteTable = useCallback(
    (event: KeyboardEvent) => {
      if (info.groupTableKey === REGULATORY_GROUP_KEY.drugProfile)
        handleKeyDownToDeleteDrugProfileTable(event);

      if (info.groupTableKey === REGULATORY_GROUP_KEY.approvalProcessContents)
        handleKeyDownToDeleteApprovalProcessContentsTable(event);
    },
    [
      handleKeyDownToDeleteApprovalProcessContentsTable,
      handleKeyDownToDeleteDrugProfileTable,
      info.groupTableKey,
    ],
  );

  useEffect(() => {
    if (focusStatus.container !== "table") return;
    window.addEventListener("keydown", handleKeyDownToDeleteTable);

    return () => {
      window.removeEventListener("keydown", handleKeyDownToDeleteTable);
    };
  }, [focusStatus, handleKeyDownToDeleteTable]);

  return {
    info,
    focusStatus,
    isFocused,
    isLastTable,
    isFocusedAddButton,
    handleClickDeleteButton,
    focusEventToChangePositionInfo,
    tableWrapperRef,
    tableCustomTitle,
    isVisibleTableCustomTitle,
    numberOfGroupShellCount,
    handleDefaultFocus,
    isEnableEdit,
    changePositionInfo,
    changeFocusStatus,
    changePositionInfoToDefault,
    handleFocusGroupShell,
  };
};
